import { CommonModule } from '@angular/common';
import {
  Component, OnInit, Input, EventEmitter, Output,
  OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef, NgZone,
  ElementRef, HostListener, ViewChildren, QueryList
} from '@angular/core';
import DropdownSelectComponent from '../dropdown-select/dropdown-select.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbDateParserFormatter, NgbDatepickerModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { CardFormComponent } from '../card-form/card-form.component';
import { CustomDateParserFormatter } from 'src/app/core/providers/date/CustomDateParserFormatter';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SavedView, SavedViewsService } from '../saved-views/saved-views.service';
import { SavedViewsComponent } from '../saved-views/saved-views.component';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-search-form',
  standalone: true,
  imports: [
    SavedViewsComponent,
    NgbDatepickerModule,
    NgbTooltipModule,
    TranslateModule,
    SharedModule,
    ReactiveFormsModule,
    CommonModule,

  ],
  templateUrl: './search-form.component.html',
  styleUrls: ['./search-form.component.scss'],
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
})
export class SearchFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() allowSavedViews = false;
  @Input() viewContext: Record<string, unknown> = {};
  @Output() viewContextChange = new EventEmitter<Record<string, unknown>>();
  private initialViewContext: Record<string, unknown> | null = null;
  private initialFilters: Record<string, unknown> = {};
  private restoredFilters: Record<string, unknown> | null = null;

  @Input() formFields: any[] = [];
  @Input() showResultsCount = true;
  @Input() searchResultsCount = 0;
  @Input() isExpanded: boolean = false;
  @Input() isExpandable: boolean = true;

  @Output() search = new EventEmitter<any>();

  searchForm: FormGroup;
  private valueChangesSub?: Subscription;
  private dependentFieldSubs: Subscription[] = [];
  private resetScheduled = false;
  private destroyed = false;
  private lastFormFieldsSignature = '';

  previousFieldValues: Record<string, any> = {};
  firstRowFields: any[] = [];
  secondRowFields: any[] = [];
  showMore: boolean = false;

  /** Chip actuellement ouvert (panel affiché) */
  activeChip: string | null = null;

  /** Références aux instances app-dropdown-select présentes dans le DOM */
  @ViewChildren(DropdownSelectComponent)
  private dropdownSelects!: QueryList<DropdownSelectComponent>;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private el: ElementRef,
    public savedViews: SavedViewsService,
    private route: ActivatedRoute,
  ) {
    this.searchForm = this.fb.group({});
  }

  // ---- Getters pour les chips ----

  /** Champ de recherche principal (isMain ou type=search) */
  get mainField(): any {
    return this.formFields.find((f: any) => f.isMain || f.type === 'search') ?? null;
  }

  /** Champs cachés */
  get hiddenFields(): any[] {
    return this.formFields.filter((f: any) => f.type === 'hidden');
  }

  /** Champs affichés sous forme de chips (tout sauf search/hidden) */
  get filterFields(): any[] {
    return this.formFields.filter((f: any) => f.type !== 'hidden' && !f.isMain && f.type !== 'search');
  }

  readonly visibleFilterCount = 3;

  get visibleFilterFields(): any[] {
    const all = this.filterFields;
    const withValue = all.filter((f) => this.hasValue(f.name));
    const withoutValue = all.filter((f) => !this.hasValue(f.name));
    const remaining = Math.max(0, this.visibleFilterCount - withValue.length);
    return [...withValue, ...withoutValue.slice(0, remaining)];
  }

  get extraFilterFields(): any[] {
    const visibleKeys = new Set(this.visibleFilterFields.map((f) => f.name));
    return this.filterFields.filter((f) => !visibleKeys.has(f.name));
  }

  get hiddenFilterCount(): number {
    return this.extraFilterFields.length;
  }

  get hiddenFiltersTooltip(): { label: string; value: string | null }[] {
    return this.extraFilterFields.map((f) => ({
      label: this.translate.instant(f.label),
      value: this.hasValue(f.name) ? this.getValueLabel(f.name) : null,
    }));
  }


  // ---- Logique des chips ----

  /** Ferme le panel actif si on clique en dehors du composant */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.activeChip = null;
    }
  }

  toggleChip(fieldName: string, event: Event): void {
    event.stopPropagation();
    const wasOpen = this.activeChip === fieldName;
    this.activeChip = wasOpen ? null : fieldName;

    if (this.activeChip !== null) {
      setTimeout(() => {
        const dropdown = this.dropdownForField(fieldName);
        if (dropdown) {
          dropdown.showDropdown();
          this.cdr.detectChanges();
        }
      }, 0);
    }
  }

  private dropdownForField(fieldName: string): DropdownSelectComponent | undefined {
    const renderedFields = this.showMore
      ? [...this.visibleFilterFields, ...this.extraFilterFields]
      : [...this.visibleFilterFields];
    const dropdownTypes = ['entity', 'multiselect', 'select'];
    const orderedDropdownFieldNames = renderedFields
      .filter((f) => dropdownTypes.includes(f.type))
      .map((f) => f.name);
    const idx = orderedDropdownFieldNames.indexOf(fieldName);
    if (idx < 0) return undefined;
    return this.dropdownSelects?.get(idx) ?? undefined;
  }

  /**
   * Lit la valeur d'un contrôle de formulaire en utilisant l'accès DIRECT par clé
   * (bracket notation) plutôt que FormGroup.get(), qui interprète les points comme
   * des séparateurs de chemin imbriqué (ex. 'company.companyStatus' échouerait).
   */
  private controlValue(fieldName: string): any {
    return this.searchForm.controls[fieldName]?.value;
  }

  hasValue(fieldName: string): boolean {
    const val = this.controlValue(fieldName);
    if (val === null || val === undefined || val === '') return false;
    // Pour les checkboxes uniquement, false = non coché = pas de valeur active.
    // Pour les autres types (select avec boolean, etc.), false est une vraie valeur.
    if (val === false) {
      const field = this.formFields.find((f: any) => f.name === fieldName);
      if (field?.type === 'checkbox') return false;
    }
    if (Array.isArray(val)) return val.filter(v => v !== null && v !== undefined && v !== '').length > 0;
    return true;
  }

  isResolvingValue(fieldName: string): boolean {
    return this.hasValue(fieldName) && this.getValueLabel(fieldName) === null;
  }

  /** Nombre de valeurs sélectionnées (pour les champs multi-select uniquement) */
  getValueCount(fieldName: string): number {
    const field = this.formFields.find((f: any) => f.name === fieldName);
    if (!field?.multiple || !this.hasValue(fieldName)) return 0;
    const val = this.controlValue(fieldName);
    if (Array.isArray(val)) return val.filter(v => v !== null && v !== undefined && v !== '').length;
    return 0;
  }

  getValueLabels(fieldName: string): string[] | null {
    const field = this.formFields.find((f: any) => f.name === fieldName);
    if (!field || !this.hasValue(fieldName)) return [];
    const val = this.controlValue(fieldName);

    const lookupInDropdowns = (v: any): string | null => {
      if (!this.dropdownSelects) return null;
      for (const ds of this.dropdownSelects.toArray()) {
        if (!Array.isArray(ds.items) || ds.items.length === 0) continue;
        const match = ds.items.find((o: any) => o?.value === v);
        if (match && match.label != null) return String(match.label);
      }
      return null;
    };

    const isLookupType = field.type === 'entity' || field.type === 'select' || field.type === 'multiselect';

    const labelFor = (v: any): string | null => {
      if (v == null) return '';
      if (field.type === 'date' && v.year && v.month && v.day) {
        return `${String(v.day).padStart(2, '0')}/${String(v.month).padStart(2, '0')}/${v.year}`;
      }
      if (typeof v === 'object') {
        const labelKey = field.optionName || 'label';
        return v[labelKey] ?? v.name ?? '';
      }
      if (Array.isArray(field.options)) {
        const valueKey = field.optionValue ?? 'value';
        const labelKey = field.optionName ?? 'label';
        const opt = field.options.find((o: any) => o?.[valueKey] === v);
        if (opt) {
          const raw = opt[labelKey];
          return field.type === 'multiselect' || field.type === 'select'
            ? this.translate.instant(raw)
            : String(raw);
        }
      }
      const fromDropdown = lookupInDropdowns(v);
      if (fromDropdown) return fromDropdown;
      return isLookupType ? null : String(v);
    };

    if (Array.isArray(val)) {
      const nonEmpty = val.filter((v) => v != null && v !== '');
      const labels = nonEmpty.map(labelFor);
      if (labels.some((l) => l === null)) return null;
      return (labels as string[]).filter(Boolean);
    }
    if (typeof val === 'boolean') return [val ? 'Oui' : 'Non'];
    const single = labelFor(val);
    if (single === null) return null;
    return single === '' ? [] : [single];
  }

  getValueLabel(fieldName: string): string | null {
    const labels = this.getValueLabels(fieldName);
    if (labels === null) return null;
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    return `${labels[0]} +${labels.length - 1}`;
  }

  chipTooltipText(fieldName: string): string {
    if (this.getValueCount(fieldName) <= 1) return '';
    const labels = this.getValueLabels(fieldName);
    return labels ? labels.join('\n') : '';
  }

  /**
   * Détermine si le bouton × doit être affiché pour un chip.
   * Masqué si le champ est `required`, s'il est explicitement `clearable: false`,
   * ou si aucune valeur n'est sélectionnée.
   */
  canClearField(field: any): boolean {
    if (field.required === true || field.clearable === false) return false;
    return this.hasValue(field.name);
  }

  clearField(event: Event, fieldName: string): void {
    event.stopPropagation();
    const field = this.formFields.find((f: any) => f.name === fieldName);
    if (!field) return;

    let emptyValue: any;
    if (field.multiple) emptyValue = [];
    else if (field.type === 'checkbox') emptyValue = false;
    else emptyValue = null;

    // Accès direct pour éviter le problème du chemin pointé
    this.searchForm.controls[fieldName]?.setValue(emptyValue);
    this.activeChip = null;
    this.cdr.markForCheck();
  }

  /**
   * Appelé sur (onSelectionChange) — chaque modification de valeur.
   * Ferme le panel uniquement pour les champs à sélection unique ;
   * pour le multi-select on attend la validation explicite.
   */
  onDropdownSelectionChange(fieldName: string): void {
    const field = this.formFields.find((f: any) => f.name === fieldName);
    if (!field?.multiple) {
      this.activeChip = null;
      this.cdr.markForCheck();
    }
  }

  /**
   * Appelé sur (onValidateSelection) — clic sur "Valider" ou clic hors dropdown.
   * Ferme le panel dans tous les cas (fin de la sélection multi ou single).
   */
  onDropdownValidate(fieldName: string): void {
    this.activeChip = null;
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.lastFormFieldsSignature = this.buildFormFieldsSignature(this.formFields);
    this.splitFields();
    this.initializeForm();
    this.setupValueChanges();        // <-- abonnement initial
    this.emitIfValid(false);         // <-- émettre l’état initial (optionnel)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formFields'] && !changes['formFields'].firstChange) {
      const nextSignature = this.buildFormFieldsSignature(this.formFields);
      if (nextSignature === this.lastFormFieldsSignature) {
        return;
      }

      this.lastFormFieldsSignature = nextSignature;
      this.scheduleFormReset();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.valueChangesSub?.unsubscribe();
    // Nettoyer toutes les subscriptions des champs dépendants
    this.dependentFieldSubs.forEach(sub => sub.unsubscribe());
    this.dependentFieldSubs = [];
  }

  // ---- helpers ----

  private setupValueChanges(): void {
    this.valueChangesSub?.unsubscribe();  // évite les fuites
    this.valueChangesSub = this.searchForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((formValue) => {
        this.updateDynamicApiFilters(formValue);
        this.emitIfValid(true);
      });
  }

  applySavedView(view: SavedView | null): void {
    this.initialViewContext ??= { ...this.viewContext };
    this.viewContextChange.emit(view?.context ?? this.initialViewContext);
    this.restoredFilters = { ...this.savedViews.normalizeResponsibleFilter(view?.filters ?? this.initialFilters) };
    const params = this.route.snapshot.queryParams;
    if (!view && this.savedViews.scope === '/my-folders' && params['search']?.trim() && !params['savedView']) {
      this.restoredFilters = Object.fromEntries(this.formFields.map(field => [
        field.name, params[field.name] ?? (field.multiple ? [] : field.type === 'checkbox' ? false : ''),
      ]));
    }
    this.activeChip = null;
    this.resetAndInitializeForm(false);
  }

  private emitIfValid(fromChanges: boolean): void {
    if (this.restoredFilters) {
      this.restoredFilters = { ...this.restoredFilters, ...this.searchForm.value };
    }
    // Toujours émettre les valeurs, même si le formulaire n'est pas complètement valide
    // Cela permet aux filtres optionnels de fonctionner
    this.search.emit(this.searchForm.value);
  }

  splitFields() {
    let total = 0;
    this.firstRowFields = [];
    this.secondRowFields = [];
    for (let field of this.formFields) {
      const col = field.col ?? 6;
      if (total + col <= 12) {
        this.firstRowFields.push(field);
        total += col;
      } else {
        this.secondRowFields.push(field);
      }
    }
  }

  resetAndInitializeForm(preserveCurrent = true): void {
    if (preserveCurrent && this.restoredFilters) {
      this.restoredFilters = { ...this.restoredFilters, ...this.searchForm.getRawValue() };
    }
    // Nettoyer les subscriptions existantes
    this.dependentFieldSubs.forEach(sub => sub.unsubscribe());
    this.dependentFieldSubs = [];

    // recrée le form + réabonnement
    this.searchForm = this.fb.group({});
    this.previousFieldValues = {};
    this.splitFields();
    this.initializeForm();
    this.setupValueChanges();     // <-- IMPORTANT
    this.emitIfValid(false);      // <-- renvoyer l'état avec les defaultValue
  }

  private scheduleFormReset(): void {
    if (this.resetScheduled) return;

    this.resetScheduled = true;
    Promise.resolve().then(() => {
      this.resetScheduled = false;
      if (this.destroyed) return;
      this.resetAndInitializeForm();
      this.cdr.detectChanges();
    });
  }

  private buildFormFieldsSignature(fields: any[]): string {
    return JSON.stringify(
      (fields ?? []).map((field: any) => ({
        name: field?.name ?? null,
        type: field?.type ?? null,
        multiple: field?.multiple ?? false,
        defaultValue: field?.defaultValue ?? null,
        apiEndpoint: field?.apiEndpoint ?? null,
        apiFilters: field?.apiFilters ?? null,
        optionValue: field?.optionValue ?? null,
        optionName: field?.optionName ?? null,
        options: field?.options ?? null,
        dependsOn: field?.dependsOn ?? null,
      }))
    );
  }

  private getDependentFieldNames(field: any): string[] {
    if (Array.isArray(field?.dependsOn)) {
      return field.dependsOn;
    }
    return field?.dependsOn ? [field.dependsOn] : [];
  }

  private getDependentSourceValues(field: any, formValue: any): any {
    const dependencyNames = this.getDependentFieldNames(field);
    if (dependencyNames.length === 0) return undefined;
    if (dependencyNames.length === 1 && !Array.isArray(field?.dependsOn)) {
      return formValue[dependencyNames[0]];
    }

    return dependencyNames.reduce((acc: Record<string, any>, name: string) => {
      acc[name] = formValue[name];
      return acc;
    }, {});
  }

  initializeForm(): void {
    // D'abord, créer tous les contrôles
    this.formFields.forEach((field) => {
      if (!(field.name in this.initialFilters)) {
        this.initialFilters[field.name] = this.allowSavedViews && this.savedViews.usesOwnFoldersHome
          && this.savedViews.scope === '/my-folders' && field.name === 'responsable'
          ? [this.savedViews.currentUserIri]
          : field.defaultValue ?? '';
      }
      let defaultValue = this.restoredFilters && field.name in this.restoredFilters
        ? this.restoredFilters[field.name] : field.defaultValue ?? '';
      // Pour le type 'select', convertir le defaultValue en string UNIQUEMENT si les
      // options utilisent des valeurs string (ex: number 2024 → '2024').
      // Ne pas convertir si les options sont déjà du même type (boolean, string…).
      if (
        field.type === 'select' &&
        defaultValue !== null && defaultValue !== '' && defaultValue !== undefined &&
        field.options?.length > 0
      ) {
        const firstOptionType = typeof field.options[0].value;
        if (firstOptionType === 'string' && typeof defaultValue !== 'string') {
          defaultValue = String(defaultValue);
        }
      }
      const control = new FormControl(defaultValue);
      this.searchForm.addControl(field.name, control);
    });

    // Ensuite, initialiser previousFieldValues et ajouter les listeners
    // (on le fait après avoir créé tous les contrôles pour s'assurer qu'ils existent)
    this.formFields.forEach((field) => {
      // Initialiser previousFieldValues pour les champs dépendants
      const dependencyNames = this.getDependentFieldNames(field);
      if (dependencyNames.length > 0) {
        // Trouver la valeur du champ source dans les formFields
        const initialValues = dependencyNames.reduce((acc: Record<string, any>, name: string) => {
          const sourceField = this.formFields.find(f => f.name === name);
          if (sourceField && sourceField.defaultValue !== undefined) {
            acc[name] = sourceField.defaultValue;
          }
          return acc;
        }, {});
        // Remote filters may still belong to the previous view: recompute them on rebuild.
        if (field.type !== 'entity' && Object.keys(initialValues).length > 0) {
          this.previousFieldValues[field.name] = JSON.stringify(initialValues);
        }

        for (const dependencyName of dependencyNames) {
          const sourceControl = this.searchForm.get(dependencyName);
          if (!sourceControl) continue;

          const sub = sourceControl.valueChanges.subscribe(() => {
            const formValue = this.searchForm.value;
            this.updateDynamicApiFilters(formValue);
          });
          this.dependentFieldSubs.push(sub);
        }
      }
    });

    // Mettre à jour les options dynamiques après l'initialisation
    const formValue = this.searchForm.value;
    this.updateDynamicApiFilters(formValue);
    if (this.restoredFilters) {
      // Dependency updates must finish before restoring the saved selection (including empty values).
      this.searchForm.patchValue(this.restoredFilters, { emitEvent: false });
    }

    // pas de valueChanges ici — il est géré dans setupValueChanges()
  }

  onFormChanges(): void {
    // (gardé pour compatibilité si d’autres appels existent, mais non utilisé)
    this.setupValueChanges();
  }

  updateDynamicApiFilters(formValue: any): void {
    let fieldsUpdated = false;
    const newFormFields = [...this.formFields]; // Créer un nouveau tableau pour forcer la détection de changement

    // Utiliser une boucle for normale au lieu de forEach pour pouvoir utiliser continue
    for (let index = 0; index < this.formFields.length; index++) {
      const field = this.formFields[index];

      const dependencyNames = this.getDependentFieldNames(field);
      if (dependencyNames.length > 0 && typeof field.mapFilters === 'function') {
        const sourceValue = this.getDependentSourceValues(field, formValue);
        // Utiliser le nom du champ dépendant comme clé pour previousFieldValues
        const sourceSignature = JSON.stringify(sourceValue ?? null);
        const previousValue = this.previousFieldValues[field.name];

        // Si la valeur source n'a pas changé, ne rien faire
        // Mais si previousValue est undefined (première fois), on doit quand même mettre à jour
        if (previousValue !== undefined && sourceSignature === previousValue) {
          newFormFields[index] = field;
          continue;
        }

        // Les filtres distants doivent aussi être recalculés lorsque leurs dépendances sont vidées.
        const isEmptyObject =
          typeof sourceValue === 'object' &&
          sourceValue !== null &&
          !Array.isArray(sourceValue) &&
          Object.values(sourceValue).every(
            (value) =>
              value === undefined ||
              value === null ||
              value === '' ||
              (Array.isArray(value) && value.length === 0)
          );
        if (field.type !== 'entity' && (
          sourceValue === undefined ||
          sourceValue === null ||
          sourceValue === '' ||
          isEmptyObject
        )) {
          newFormFields[index] = field;
          continue;
        }

        // Stocker la nouvelle valeur source avec le nom du champ dépendant comme clé
        this.previousFieldValues[field.name] = sourceSignature;
        const mappedValue = field.mapFilters(sourceValue);

        // Si mapFilters retourne un tableau (options pour select), mettre à jour les options
        if (Array.isArray(mappedValue)) {
          // Vérifier si les options ont réellement changé pour éviter les re-renders inutiles
          const currentOptions = field.options || [];
          let optionsChanged = false;

          if (currentOptions.length !== mappedValue.length) {
            optionsChanged = true;
          } else {
            // Comparer chaque option par valeur et label
            for (let i = 0; i < currentOptions.length; i++) {
              const currentOpt = currentOptions[i];
              const newOpt = mappedValue[i];
              if (!newOpt ||
                  String(currentOpt?.value) !== String(newOpt?.value) ||
                  String(currentOpt?.label) !== String(newOpt?.label)) {
                optionsChanged = true;
                break;
              }
            }
          }

          if (!optionsChanged) {
            // Les options n'ont pas changé, ne pas mettre à jour
            newFormFields[index] = field;
            continue;
          }

          fieldsUpdated = true;

          // Vérifier la valeur actuelle du champ dans le formulaire
          const currentFieldValue = formValue[field.name];
          const fieldWasEmpty = !currentFieldValue || currentFieldValue === '' || currentFieldValue === null || currentFieldValue === undefined;

          // Déterminer la nouvelle valeur par défaut
          let newDefaultValue: string | null = null;
          if (field.mapDefaultValue && typeof field.mapDefaultValue === 'function') {
            // Si mapDefaultValue est défini, l'utiliser (comportement pour planning)
            newDefaultValue = field.mapDefaultValue(sourceValue);
            // Vérifier si la valeur par défaut existe dans les options
            const defaultExists = newDefaultValue && mappedValue.some((opt: any) => String(opt.value) === String(newDefaultValue));
            if (!defaultExists && mappedValue.length > 0) {
              // Utiliser la dernière option (31/08 de l'année sélectionnée)
              newDefaultValue = mappedValue[mappedValue.length - 1].value;
            }
          } else if (!fieldWasEmpty && mappedValue.length > 0) {
            // Si le champ avait déjà une valeur, on peut essayer de la conserver
            // Vérifier si la valeur actuelle existe dans les nouvelles options
            const currentValueExists = mappedValue.some((opt: any) => String(opt.value) === String(currentFieldValue));
            if (currentValueExists) {
              // Conserver la valeur actuelle si elle existe dans les nouvelles options
              newDefaultValue = currentFieldValue;
            } else if (field.defaultValue) {
              // Si la valeur actuelle n'existe plus, utiliser defaultValue du champ si défini
              newDefaultValue = field.defaultValue;
            }
            // Si fieldWasEmpty est true, on ne définit pas de newDefaultValue (reste null)
          }

          // Créer le champ mis à jour avec les nouvelles options et la nouvelle valeur par défaut
          const updatedField: any = {
            ...field,
            options: [...mappedValue],
            defaultValue: newDefaultValue || field.defaultValue
          };

          // Stocker la valeur à définir pour ce champ
          // Seulement si newDefaultValue est défini ET que le champ n'était pas vide
          // OU si mapDefaultValue existe (comportement explicite)
          if (newDefaultValue && (field.mapDefaultValue || !fieldWasEmpty)) {
            updatedField._valueToSet = newDefaultValue;
          }

          newFormFields[index] = updatedField;
        } else if (JSON.stringify(field.apiFilters ?? {}) !== JSON.stringify(mappedValue)) {
          newFormFields[index] = { ...field, apiFilters: mappedValue };
          fieldsUpdated = true;
        }
      } else {
        // S'assurer que tous les champs sont copiés dans newFormFields
        newFormFields[index] = field;
      }
    }

    // Mettre à jour formFields seulement si des changements ont été détectés
    if (fieldsUpdated) {
      // Collecter les valeurs à mettre à jour AVANT de modifier formFields
      const valuesToUpdate: Record<string, string> = {};

      // Parcourir les champs mis à jour pour collecter les valeurs à définir
      newFormFields.forEach((updatedField) => {
        if (this.getDependentFieldNames(updatedField).length > 0 && typeof updatedField.mapFilters === 'function') {
          const valueToSet = (updatedField as any)?._valueToSet;
          if (valueToSet) {
            valuesToUpdate[updatedField.name] = valueToSet;
          }
        }
      });

      // Mettre à jour formFields d'abord pour que les nouvelles options soient disponibles
      this.formFields = newFormFields;

      // Mettre à jour aussi firstRowFields et secondRowFields pour que le template se mette à jour
      // Mettre à jour les références dans firstRowFields et secondRowFields pour pointer vers les nouveaux objets
      this.splitFields();

      // Mettre à jour les références dans firstRowFields et secondRowFields
      // pour pointer vers les nouveaux objets de newFormFields (qui ont les nouvelles options)
      this.firstRowFields = this.firstRowFields.map(field => {
        const updatedField = newFormFields.find(f => f.name === field.name);
        return updatedField || field;
      });

      this.secondRowFields = this.secondRowFields.map(field => {
        const updatedField = newFormFields.find(f => f.name === field.name);
        return updatedField || field;
      });

      // Mettre à jour les valeurs dans le même cycle si possible
      if (Object.keys(valuesToUpdate).length > 0) {
        // S'assurer que tous les contrôles existent
        Object.keys(valuesToUpdate).forEach((fieldName) => {
          let control = this.searchForm.get(fieldName);
          if (!control) {
            const field = newFormFields.find(f => f.name === fieldName);
            if (field) {
              control = new FormControl(valuesToUpdate[fieldName]);
              this.searchForm.addControl(fieldName, control);
            }
          }
        });

        // Utiliser patchValue pour mettre à jour toutes les valeurs en une fois
        this.searchForm.patchValue(valuesToUpdate, { emitEvent: false });

        // Marquer les contrôles comme touchés
        Object.keys(valuesToUpdate).forEach((fieldName) => {
          const control = this.searchForm.get(fieldName);
          if (control) {
            control.markAsTouched();
            control.updateValueAndValidity({ emitEvent: false });
          }
        });
      }

      // Forcer la détection de changement une seule fois après toutes les mises à jour
      this.ngZone.run(() => {
        this.cdr.detectChanges();
      });
    }
  }

  toggleShowMore(): void {
    this.showMore = !this.showMore;
  }

  trackByOptionValue(index: number, option: any): any {
    return option?.value ?? index;
  }

  toggleSearch(): void {
    this.isExpanded = !this.isExpanded;
  }

  onSearch(): void {
    this.emitIfValid(false);
  }

  get formControls() {
    return this.searchForm.controls;
  }
}
